import { useState, useEffect, useRef } from 'react';

interface Options {
  pingUrl?: string;
  intervalMs?: number;
  timeoutMs?: number;
}

export function useNetworkStatus({
  pingUrl = '/api/healthcheck',
  intervalMs = 5000,
  timeoutMs = 3000
}: Options = {}) {
  const [online, setOnline] = useState(true);
  const lastState = useRef<boolean>(true);
  const intervalId = useRef<number | null>(null);

  const pingCheck = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

      const res = await fetch(pingUrl, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (res.ok) {
        if (!lastState.current) {
          lastState.current = true;
          setOnline(true);
        }
      } else {
        throw new Error(`Status ${res.status}`);
      }
    } catch {
      if (lastState.current) {
        lastState.current = false;
        setOnline(false);
      }
    }
  };

  useEffect(() => {
    const goOnline = () => pingCheck();
    const goOffline = () => {
      lastState.current = false;
      setOnline(false);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // initial check + polling
    pingCheck();
    intervalId.current = window.setInterval(pingCheck, intervalMs);

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      if (intervalId.current) clearInterval(intervalId.current);
    };
  }, [pingUrl, intervalMs, timeoutMs]);

  // Optional: listen to SW offline signals
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (ev) => {
        if (ev.data?.type === 'OFFLINE_DETECTED') {
          console.log('SW DETECTED OFFLINE');

          lastState.current = false;
          setOnline(false);
        }
      });
    }
  }, []);

  return online;
}
